import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Search, Camera, DollarSign } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import API from "../../../util/API";
import {
  fetchSearchHistory,
  getOrCreateDeviceId,
  mergeSearchHistory,
  recordSearch,
  readLocalSearchHistory,
  writeLocalSearchHistory,
} from "../../../util/searchHistory";
import { hasPersonalizationConsent, subscribeCookieConsent } from "../../../util/privacyConsent";
import { buildProductPath, buildProductRouteState } from "../../../util/productSlug";

const SearchBar = ({ className = "", autoFocusInput = false }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestStatus, setSuggestStatus] = useState("idle");
  const suggestRequestRef = useRef(0);
  const suggestTimerRef = useRef(null);
  const navigate = useNavigate();
  const { shipToCountry, currency } = useSelector((state) => state.userSettings || {});
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (autoFocusInput) {
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [autoFocusInput]);
  const searchContext = useMemo(() => {
    const country = String(shipToCountry || "").trim().toUpperCase();
    const cur = String(currency || "").trim().toUpperCase();
    return { country, currency: cur };
  }, [shipToCountry, currency]);

  const handleSearch = (e) => {
    e.preventDefault();
    const trimmed = searchTerm.trim();
    if (!trimmed) return;
    recordSearch({
      query: trimmed,
      country: searchContext.country,
      currency: searchContext.currency,
    }).then((next) => setHistoryItems(next));
    navigate(`/search/${encodeURIComponent(trimmed)}`);
    setIsOpen(false);
  };

  const handleImageSearch = () => {
    alert("Image search coming soon");
  };

  const loadHistory = useCallback(async () => {
    const local = readLocalSearchHistory();
    setHistoryItems(local);

    try {
      const remote = await fetchSearchHistory({
        limit: 10,
        country: searchContext.country,
        currency: searchContext.currency,
      });
      const merged = hasPersonalizationConsent() ? mergeSearchHistory(local, remote, 10) : [];
      setHistoryItems(merged);
      writeLocalSearchHistory(merged);
    } catch {
      // Keep local history only.
    }

    setHistoryLoaded(true);
  }, [searchContext.country, searchContext.currency]);

  useEffect(() => {
    getOrCreateDeviceId();
  }, []);

  useEffect(
    () =>
      subscribeCookieConsent(() => {
        setHistoryItems(readLocalSearchHistory());
        setHistoryLoaded(false);
      }),
    []
  );

  useEffect(() => {
    if (!isOpen) return;
    if (!historyLoaded) {
      loadHistory();
    }
  }, [isOpen, historyLoaded, historyItems.length, loadHistory]);

  useEffect(() => {
    setHistoryLoaded(false);
  }, [searchContext.country, searchContext.currency]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [isOpen]);

  const trimmedSearch = searchTerm.trim();
  const filteredHistory = useMemo(() => {
    if (!trimmedSearch) return historyItems;
    const needle = trimmedSearch.toLowerCase();
    return historyItems.filter((item) => String(item.query || "").toLowerCase().includes(needle));
  }, [historyItems, trimmedSearch]);

  const categorySuggestions = useMemo(() => {
    if (!trimmedSearch) return [];
    const needle = trimmedSearch.toLowerCase();
    return ["Men", "Women", "Electronics", "Home", "Beauty"].filter((c) =>
      c.toLowerCase().includes(needle)
    );
  }, [trimmedSearch]);

  const handleSelect = (value) => {
    const term = String(value || "").trim();
    if (!term) return;
    setSearchTerm(term);
    recordSearch({
      query: term,
      country: searchContext.country,
      currency: searchContext.currency,
    }).then((next) => setHistoryItems(next));
    navigate(`/search/${encodeURIComponent(term)}`);
    setIsOpen(false);
  };

  const handleClearHistory = () => {
    writeLocalSearchHistory([]);
    setHistoryItems([]);
    setHistoryLoaded(true);
  };

  const handleRemoveHistory = (entry) => {
    const next = historyItems.filter(
      (item) =>
        !(
          item.query === entry.query &&
          item.country === entry.country &&
          item.currency === entry.currency
        )
    );
    writeLocalSearchHistory(next);
    setHistoryItems(next);
    if (next.length === 0) setHistoryLoaded(true);
  };

  useEffect(() => {
    if (!trimmedSearch) {
      setSuggestions([]);
      setSuggestStatus("idle");
      return;
    }

    clearTimeout(suggestTimerRef.current);
    setSuggestStatus("loading");
    const requestId = ++suggestRequestRef.current;

    suggestTimerRef.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          q: trimmedSearch,
          country: searchContext.country || "US",
          currency: searchContext.currency || "USD",
        });
        const response = await API.get(`/product/suggest?${params.toString()}`);
        if (suggestRequestRef.current !== requestId) return;
        setSuggestions(response?.data?.suggestions || []);
        setSuggestStatus("succeeded");
      } catch {
        if (suggestRequestRef.current !== requestId) return;
        setSuggestions([]);
        setSuggestStatus("failed");
      }
    }, 250);

    return () => clearTimeout(suggestTimerRef.current);
  }, [trimmedSearch, searchContext.country, searchContext.currency]);

  return (
    <div
      className={["w-full", className, "relative"].join(" ").trim()}
      ref={rootRef}
      style={{ fontFamily: '"Segoe UI", "Helvetica Neue", Arial, sans-serif' }}
    >
      <form onSubmit={handleSearch} className="relative w-full">
        <div className="flex h-11 w-full items-center overflow-hidden rounded-md border border-slate-300 bg-white pl-3 transition focus-within:border-slate-400 focus-within:ring-1 focus-within:ring-slate-300">
          <input
            ref={inputRef}
            type="text"
            placeholder="Search Cherrfy products"
            className="h-full min-w-0 flex-1 bg-transparent pr-3 text-sm font-normal text-slate-800 placeholder:text-slate-400 outline-none"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => setIsOpen(true)}
          />

          <button
            type="button"
            onClick={handleImageSearch}
            disabled
            className="hidden h-9 w-9 items-center justify-center border-l border-slate-200 text-slate-600 transition hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-60 sm:inline-flex"
            aria-label="Image search coming soon"
          >
            <Camera className="h-5 w-5" strokeWidth={2.4} />
          </button>

          <button
            type="submit"
            className="inline-flex h-9 min-w-[48px] items-center justify-center border-l border-slate-200 px-3 text-slate-800 transition-colors hover:text-slate-950"
            aria-label="Search"
          >
            <Search className="h-[21px] w-[21px]" strokeWidth={2.4} />
          </button>
        </div>
      </form>

      {isOpen && (trimmedSearch || filteredHistory.length > 0) ? (
        <div className="absolute left-0 top-full z-50 mt-1.5 w-full overflow-hidden rounded-md border border-slate-200/70 bg-white shadow-none">
          <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-4 py-2.5">
            <span className="text-[11px] font-medium tracking-wide text-slate-700">
              {trimmedSearch ? "Suggestions" : "Recent Searches"}
            </span>
            {!trimmedSearch && filteredHistory.length > 0 ? (
              <button
                type="button"
                onClick={handleClearHistory}
                className="text-[11px] font-normal text-slate-700 hover:text-slate-900"
              >
                Clear
              </button>
            ) : null}
          </div>

          <div className="max-h-[360px] overflow-y-auto py-2">
            {trimmedSearch ? (
              <div className="px-2.5">
                {suggestStatus === "loading" ? (
                  <div className="px-2 py-3 text-xs text-slate-500">Searching...</div>
                ) : suggestions.length > 0 ? (
                  <div className="space-y-1">
                    {suggestions.map((suggestion, index) => {
                      if (suggestion?.type === "query") {
                        return (
                          <button
                            key={`query-${suggestion?.query || index}`}
                            type="button"
                            onClick={() => handleSelect(suggestion?.query)}
                            className="group flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50"
                          >
                            <Search className="h-4 w-4 shrink-0 text-slate-400" />
                            <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-900 group-hover:text-slate-950">
                              {suggestion?.query}
                            </span>
                          </button>
                        );
                      }
                      const productId =
                        suggestion?.productId || suggestion?.product_id || suggestion?._id || suggestion?.id;
                      const title = suggestion?.title || suggestion?.name || "Product";
                      const image =
                        typeof suggestion?.image === "string" && suggestion.image
                          ? suggestion.image
                          : Array.isArray(suggestion?.images) && suggestion.images[0]
                            ? suggestion.images[0]
                            : "";
                      return (
                        <button
                          key={`product-${productId || index}`}
                          type="button"
                          onClick={() => {
                            if (!productId) return;
                            setIsOpen(false);
                            navigate(
                              buildProductPath({ productId, name: title }),
                              {
                                state: buildProductRouteState({ productId }),
                              }
                            );
                          }}
                          className="group flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50"
                        >
                          <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                            {image ? (
                              <img
                                src={image}
                                alt={title}
                                className="h-full w-full object-cover"
                                loading="lazy"
                              />
                            ) : (
                              <Search className="h-4 w-4 text-slate-400" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-slate-900 group-hover:text-slate-950">
                              {title}
                            </p>
                            {suggestion?.price != null && suggestion.price !== "" ? (
                              <p className="text-xs text-[#D2042D] truncate inline-flex items-center gap-1">
                                <DollarSign className="h-3 w-3" aria-hidden="true" />
                                {suggestion.price}
                              </p>
                            ) : null}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="px-2 py-3 text-xs text-slate-500">
                    {suggestStatus === "failed"
                      ? "Couldn’t load suggestions. Check your connection."
                      : "No products found."}
                  </div>
                )}
              </div>
            ) : filteredHistory.length > 0 ? (
              <div className="px-4 flex flex-wrap gap-2">
                {filteredHistory.map((item) => (
                  <span
                    key={`${item.query}-${item.country || ""}-${item.currency || ""}`}
                    className="inline-flex items-center gap-2 rounded-md border border-slate-200/70 bg-white px-3 py-1 text-xs font-normal text-slate-700"
                  >
                    <button
                      type="button"
                      onClick={() => handleSelect(item.query)}
                      className="max-w-[160px] truncate"
                      title={item.query}
                    >
                      {item.query}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveHistory(item)}
                      className="text-slate-400 hover:text-slate-800"
                      aria-label={`Remove ${item.query}`}
                    >x</button>
                  </span>
                ))}
              </div>
            ) : null}

            {!trimmedSearch && categorySuggestions.length > 0 ? (
              <div className="pt-2">
                <div className="px-4 pb-2 text-[11px] font-medium uppercase tracking-[0.14em] text-slate-400">
                  Categories
                </div>
                <div className="px-4 flex flex-wrap gap-2">
                  {categorySuggestions.map((category) => (
                    <button
                      key={category}
                      type="button"
                      onClick={() => handleSelect(category)}
                      className="inline-flex items-center rounded-md border border-slate-200/70 bg-white px-3 py-1 text-xs font-normal text-slate-700 hover:bg-slate-50"
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SearchBar;

